/**
 * Header-based session ID fallback for cross-site deployments (nano.fitracking.com
 * talking to api.tryfi.com). Chrome blocks third-party cookies in this scenario, so the
 * fi.sid session cookie never reaches the API. The backend already supports an
 * x-session-id header as an alternative — this module persists the session ID returned
 * in auth responses and attaches it to subsequent requests.
 *
 * Storage tiers (in priority order for reads):
 *   1. In-memory module variable. Source of truth for the current page lifetime.
 *      Survives any storage failure as long as setSessionId was called in this tab.
 *   2. localStorage. Best-effort persistence across reloads. On iOS Safari Private
 *      Browsing, certain in-app WebViews, and some sandboxed contexts, reads return
 *      null or writes throw QuotaExceededError — the in-memory tier keeps the user's
 *      in-flight flow working when this happens.
 *   3. Parent-domain cookie. Written so sibling sites (e.g. shop.fitracking.com) can
 *      read the session on navigation; not consulted for reads on this site.
 */

import Cookies from 'js-cookie';
import { domainConfig } from './domainConfig';

const STORAGE_KEY = 'fi_session_id';

// In-memory cache. Treated as the source of truth for reads — see module docstring.
let memorySessionId: string | null = null;

// Telemetry de-dupe: only emit the storage-unavailable analytics event once per page
// load, regardless of how many reads/writes fail.
let storageFailureReported = false;

function safeReadStorage(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch (err) {
    reportStorageFailure('read', err);
    return null;
  }
}

function safeWriteStorage(id: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, id);
  } catch (err) {
    reportStorageFailure('write', err);
  }
}

function safeRemoveStorage(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // If we couldn't write, we likely can't remove either. The in-memory clear in
    // clearSessionId is what actually invalidates the session for this tab.
  }
}

function reportStorageFailure(operation: 'read' | 'write', err: unknown): void {
  if (storageFailureReported) {
    return;
  }
  storageFailureReported = true;

  // Dynamic import to avoid a circular dependency: analytics → store → reducers
  // → sessionHeader. Resolving the import lazily means the module graph at boot
  // is unchanged; we only pay the cost when storage actually fails.
  void import('./analytics')
    .then(({ default: analytics }) => {
      analytics.track('Session Storage Unavailable', {
        operation,
        message: err instanceof Error ? err.message : String(err),
      });
    })
    .catch(() => {
      // Never let analytics import or send failures escape — this is a best-effort
      // observability hook, not a critical path.
    });
}

// Cookie domain is configured per-deploy via VITE_SESSION_COOKIE_DOMAIN (see
// cdk/app.ts). Empty string = skip the cookie write entirely (e.g. local dev,
// or any deploy that hasn't explicitly opted in to cross-site session sharing).
function getCookieDomain(): string | undefined {
  return domainConfig.sessionCookieDomain || undefined;
}

function setParentDomainSessionCookie(id: string): void {
  const domain = getCookieDomain();
  if (!domain) {
    return;
  }
  Cookies.set(STORAGE_KEY, id, {
    expires: 30,
    domain,
    secure: window.location.protocol === 'https:',
    sameSite: 'lax',
    path: '/',
  });
}

// Seed the in-memory cache from localStorage at module load. Always overwrite the
// parent-domain cookie too — otherwise a stale value left by a sibling site would
// survive and break the cross-site handoff.
memorySessionId = safeReadStorage();
if (memorySessionId) {
  setParentDomainSessionCookie(memorySessionId);
}

export function getSessionId(): string | null {
  return memorySessionId;
}

export function setSessionId(id: string): void {
  memorySessionId = id;
  safeWriteStorage(id);
  setParentDomainSessionCookie(id);
}

export function clearSessionId(): void {
  memorySessionId = null;
  safeRemoveStorage();

  // Remove both the parent-domain cookie we set and any legacy host-only cookie
  // older code paths may have left behind, so logout fully resets session state.
  const domain = getCookieDomain();
  if (domain) {
    Cookies.remove(STORAGE_KEY, { domain, path: '/' });
  }
  Cookies.remove(STORAGE_KEY, { path: '/' });
}
